'use client';

import { BlogProfileTab } from '@/components/blog/BlogProfileTab';
import { AdvocateProductPitch } from '@/components/professionals/AdvocateProductPitch';
import { localizeSakSubtype } from '@/components/professionals/ProfessionalSubtypeBadge';
import { RegistryStatusBadge } from '@/components/professionals/RegistryStatusBadge';
import { RevealContactRow } from '@/components/professionals/RevealContactRow';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { useAuthUser } from '@/hooks/useAuthUser';
import { Link, getPathname } from '@/i18n/routing';
import { getCountryName } from '@/lib/utils/countryNames';
import { BlogService } from '@/services/blogService';
import professionalsServices from '@/services/professionals-services';
import type { BlogPost } from '@/types/blog';
import type {
  ProfessionType,
  ProfessionalRead,
} from '@/types/professionals-types';
import {
  AlertTriangle,
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  Clock,
  Crown,
  ExternalLink,
  Globe,
  Info,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Settings,
  Shield,
  Smartphone,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

const ProfileMiniMap = dynamic(
  () => import('@/components/regions/ProfileMiniMap'),
  { ssr: false }
);

/**
 * Get initials from full name.
 * For Slovak names with surname first (UPPERCASE), extracts first letter of first name and surname.
 */
function getInitials(name: string): string {
  // Remove academic titles at start and end
  const cleanName = name
    .replace(/^(JUDr\.|Mgr\.|Ing\.|PhDr\.|Dr\.|doc\.|prof\.)\s*/gi, '')
    .replace(/,?\s*(PhD\.|CSc\.|DrSc\.|JUDr\.|Mgr\.|Ing\.)$/gi, '')
    .trim();

  const parts = cleanName.split(/\s+/).filter((p) => p.length > 0);

  if (parts.length >= 2) {
    // For Slovak names, surname often comes first (UPPERCASE), first name second
    // Check if first part is all uppercase (likely surname)
    const firstPart = parts[0] ?? '';
    const secondPart = parts[1] ?? '';

    if (firstPart === firstPart.toUpperCase() && firstPart.length > 2) {
      // Surname first format: "SMITH John" -> "JS"
      return `${secondPart[0] ?? ''}${firstPart[0] ?? ''}`.toUpperCase();
    } else {
      // First name first format: "John Smith" -> "JS"
      return `${firstPart[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase();
    }
  }

  return cleanName.slice(0, 2).toUpperCase();
}

/**
 * Get profession label based on type
 */
function getProfessionLabel(
  type: ProfessionType,
  t: ReturnType<typeof useTranslations>
): string {
  const key = `professionTypes.${type}` as const;
  try {
    return t(key);
  } catch {
    return type;
  }
}

interface LawyerProfileViewProps {
  professionalId: string;
  locale: string;
  /**
   * PUBLIC record fetched server-side by the page — seeds the first render
   * so the profile content is in the crawlable initial HTML instead of a
   * skeleton. Null when the server fetch failed/404'd (the client fetch
   * then owns the error UI).
   */
  initialProfessional?: ProfessionalRead | null;
}

/**
 * Advocate profile page — reuses the professional profile UI but links back to /advokati.
 * Client component driven by props from the server page (which handles the UUID→slug
 * redirect and SSR). Renders the server-fetched public record immediately and
 * re-fetches with the viewer's auth to upgrade tier-gated fields.
 */
export default function LawyerProfileView({
  professionalId,
  locale,
  initialProfessional = null,
}: LawyerProfileViewProps) {
  const router = useRouter();
  const t = useTranslations('Lawyers');
  // #4541 — localized SAK registry subtype labels (Advokát / Usadený
  // euroadvokát / Medzinárodný advokát).
  const tSakSubtype = useTranslations('Lawyers.sakSubtype');

  const { isLoading: isAuthLoading } = useAuth();
  const { isProfessional, professionalId: myProfessionalId } = useAuthUser();

  const [professional, setProfessional] = useState<ProfessionalRead | null>(
    initialProfessional
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Advocate blog — pre-fetched preview (top 3 published posts) so the
  // `BlogProfileTab` can lazy-render on the profile. Keeping it as a separate
  // effect from the professional fetch means a blog-API hiccup never blocks
  // the profile from rendering.
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);

  useEffect(() => {
    // Wait for auth to finish loading before making API calls
    if (isAuthLoading) return;

    async function fetchProfessional() {
      setIsLoading(true);
      setError(null);

      try {
        const response =
          await professionalsServices.getProfessionalByIdOrSlug(professionalId);

        if (response.status === 'success' && response.data) {
          setProfessional(response.data);
        } else if (initialProfessional === null) {
          // Only surface the error UI when there's no server-seeded record —
          // a failed auth-upgrade re-fetch must not blank an already-rendered
          // public profile.
          setError(t('profile.notFound'));
        }
      } catch (err) {
        console.error('Error fetching professional:', err);
        if (initialProfessional === null) {
          setError(t('profile.loadError'));
        }
      } finally {
        setIsLoading(false);
      }
    }

    if (professionalId) {
      fetchProfessional();
    }
  }, [professionalId, t, isAuthLoading, initialProfessional]);

  useEffect(() => {
    // Fire-and-forget preview fetch for the in-profile Blog tab. `locale`
    // comes from the URL (`params.locale`) and is restricted to `en` | `sk`
    // — `normalisePostLocale` in `blog/[slug]/page.tsx` does the same dance
    // so the visible posts match what the public listing would show.
    if (!professionalId) return;
    let cancelled = false;
    // The advocate's blog is shown regardless of the current UI language — a
    // Slovak-only author must still surface on their English profile (and vice
    // versa). Passing `null` for the locale lists posts in every language; each
    // card carries its own locale (badge + `?locale=` link) so "Read more"
    // resolves to the correct post. The dedicated /blog index stays
    // locale-scoped for SEO — this preview is intentionally language-agnostic.
    BlogService.getInstance()
      .listPublicPosts(professionalId, null, 3, 0)
      .then((resp) => {
        if (cancelled) return;
        if (resp?.status === 'success' && resp.data) {
          setBlogPosts(resp.data.items);
        }
      })
      .catch(() => {
        // Silent — the BlogProfileTab renders nothing when posts is empty.
      });
    return () => {
      cancelled = true;
    };
  }, [professionalId, locale]);

  const handleBack = () => {
    router.back();
  };

  const browseAllPath = getPathname({
    locale,
    href: '/advokati',
  });

  // Skeleton only when there's nothing to show yet — with a server-seeded
  // record the full profile renders immediately (and in the SSR HTML), while
  // the auth-upgrade re-fetch runs silently in the background.
  if ((isAuthLoading || isLoading) && !professional) {
    return <ProfessionalProfileSkeleton />;
  }

  if (error || !professional) {
    return (
      <div className='container mx-auto px-4 py-8 max-w-5xl'>
        <Button variant='ghost' onClick={handleBack} className='mb-4'>
          <ArrowLeft className='mr-2 h-4 w-4' />
          {t('profile.back')}
        </Button>
        <Card>
          <CardContent className='py-16 text-center'>
            <p className='text-content-muted'>
              {error || t('profile.notFound')}
            </p>
            <Button
              variant='outline'
              onClick={() => router.push(browseAllPath)}
              className='mt-4'
            >
              {t('profile.browseAll')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwnProfile = isProfessional && myProfessionalId === professional.id;

  const isSuspended = professional.status === 'suspended';
  const isRemoved = professional.status === 'removed';
  const isRestricted = isSuspended || isRemoved;

  // Date to show as "last verified" in tombstone banners
  const verifiedDateStr = professional.deprecated_at
    ? new Date(professional.deprecated_at).toLocaleDateString(
        locale === 'sk' ? 'sk-SK' : 'en-US'
      )
    : professional.last_synced_at
      ? new Date(professional.last_synced_at).toLocaleDateString(
          locale === 'sk' ? 'sk-SK' : 'en-US'
        )
      : null;

  // "Aktualizované" shows when profile CONTENT last meaningfully changed
  // (owner edit, claim, or a SAK sync write that altered a content field) —
  // NOT last_synced_at, which the daily SAK sweep bumps for every active
  // lawyer regardless of change (everyone read "updated yesterday").
  // last_synced_at stays correct for verifiedDateStr above (registry
  // verification, not content freshness).
  const contentUpdatedAt =
    professional.content_updated_at ?? professional.created_at;

  const hasContactInfo =
    !isRestricted &&
    (professional.email ||
      professional.phone ||
      professional.mobile ||
      professional.website);

  const hasAddress = !isRemoved && (professional.city || professional.street);

  const sakUrl =
    professional.registry_source === 'sak' ? 'https://www.sak.sk' : null;

  const sakProfileUrl =
    professional.registry_source === 'sak'
      ? (professional.source_url ?? 'https://www.sak.sk/web/sk/cms/lawyer/adv')
      : null;

  return (
    <div className='container mx-auto px-4 py-6 max-w-5xl'>
      {/* Back button */}
      <Button
        variant='ghost'
        onClick={handleBack}
        className='mb-4 -ml-2 text-content-secondary hover:text-content-primary'
      >
        <ArrowLeft className='mr-2 h-4 w-4' />
        {t('profile.back')}
      </Button>

      {/* Tombstone banner for suspended profiles */}
      {isSuspended && (
        <Alert className='mb-4 border-status-warning/50 bg-status-warning/5'>
          <AlertTriangle className='h-4 w-4 text-status-warning-text' />
          <AlertTitle className='text-status-warning-text'>
            {t('tombstone.suspendedTitle')}
          </AlertTitle>
          <AlertDescription className='text-content-secondary'>
            {t('tombstone.suspendedBody')}
            {verifiedDateStr && (
              <span className='block mt-1 text-xs text-content-muted'>
                {t('tombstone.suspendedVerifiedAt')} {verifiedDateStr}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Tombstone banner for removed profiles */}
      {isRemoved && (
        <Alert className='mb-4 border-status-error/30 bg-status-error/5'>
          <XCircle className='h-4 w-4 text-status-error-text' />
          <AlertTitle className='text-status-error-text'>
            {t('tombstone.removedTitle')}
          </AlertTitle>
          <AlertDescription className='text-content-secondary'>
            {t('tombstone.removedBody')}
            {verifiedDateStr && (
              <span className='block mt-1 text-xs text-content-muted'>
                {t('tombstone.removedVerifiedAt')} {verifiedDateStr}
              </span>
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Claim-your-profile banner — PUBLIC on unclaimed, active profiles.
          The most likely claimant is an anonymous advocate who just found
          their own listing, so the CTA (funnel entrance) must be visible to
          everyone; the sensitive action (email-send / token / completion)
          stays authenticated + MFA-gated + rate-limited downstream, so making
          the entrance public leaks nothing. Supersedes the earlier
          isAuthenticated gate (#5037), which hid the funnel from exactly the
          visitors who convert. No auth-dependent branch here means no
          hydration flash either. */}
      {!professional.is_claimed && !isSuspended && !isRemoved && (
        <Card className='mb-4 border-brand-tertiary/40 bg-brand-tertiary/5'>
          <CardContent className='flex flex-col sm:flex-row items-start sm:items-center gap-3 p-4'>
            <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-tertiary/15'>
              <UserPlus className='h-5 w-5 text-brand-tertiary' />
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-semibold text-content-primary'>
                {t('profile.claimBanner.title', { name: professional.name })}
              </p>
              <p className='text-xs text-content-secondary mt-0.5'>
                {t('profile.claimBanner.description')}
              </p>
            </div>
            <Button
              asChild
              size='sm'
              className='bg-brand-tertiary hover:bg-brand-tertiary-hover text-white shrink-0 w-full sm:w-auto'
            >
              <Link
                href={{
                  pathname: '/professional-directory/claim/[id]',
                  params: { id: professional.id },
                }}
              >
                {t('profile.claimBanner.cta')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Hero Section */}
      <Card className='mb-6 overflow-hidden'>
        <CardContent className='p-0'>
          <div className='p-6 sm:p-8'>
            {isOwnProfile && (
              <div className='mb-6 flex justify-end'>
                <Button
                  variant='brandOutline'
                  size='sm'
                  asChild
                  className='gap-2'
                >
                  <Link href='/advokati'>
                    <Settings className='h-4 w-4' />
                    {t('profile.manageProfileCta')}
                  </Link>
                </Button>
              </div>
            )}
            <div
              className='flex flex-col lg:flex-row gap-6 lg:gap-8'
              data-tour='profile-hero'
            >
              {/* Left: Avatar and basic info */}
              <div className='flex flex-col sm:flex-row gap-5 flex-1'>
                {/* Avatar */}
                <div className='flex-shrink-0'>
                  <Avatar className='h-28 w-28 sm:h-32 sm:w-32 mx-auto sm:mx-0 ring-4 ring-surface-subtle'>
                    {professional.photo_url ? (
                      <AvatarImage
                        src={professional.photo_url}
                        alt={professional.name}
                      />
                    ) : null}
                    <AvatarFallback className='bg-brand-tertiary/10 text-brand-tertiary text-3xl font-semibold'>
                      {getInitials(professional.name)}
                    </AvatarFallback>
                  </Avatar>
                </div>

                {/* Info */}
                <div className='flex-1 text-center sm:text-left'>
                  {/* Name and verification */}
                  <div className='flex flex-wrap items-center justify-center sm:justify-start gap-x-2 gap-y-1.5 mb-1'>
                    <h1 className='text-2xl sm:text-3xl font-bold text-content-primary'>
                      {professional.name}
                    </h1>
                    {professional.is_claimed && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-tertiary text-white ring-1 ring-brand-tertiary shadow-sm'>
                              <Check className='h-3.5 w-3.5 stroke-[3]' />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side='top'>
                            {t('profile.card.claimed')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {/* #6555 — SAK registry standing at-a-glance next to the
                        identity. Only surface the suspended/removed states here:
                        an "active" pill would be a second green check right next
                        to the claimed verified mark (visual duplication), so we
                        let the claimed check alone signal good standing. */}
                    <RegistryStatusBadge status={professional.status} />
                  </div>

                  {/* Profession label.
                      #4541: `professional.title` now holds the derived
                      academic-title prefix (e.g. "JUDr."), not the SAK
                      registry subtype. Show the localized SAK subtype
                      when present ("Advokát" / "Usadený euroadvokát" /
                      "Medzinárodný advokát"), falling back to the
                      localized profession_type label. */}
                  <p className='text-lg text-content-secondary mb-3'>
                    {localizeSakSubtype(
                      professional.type,
                      tSakSubtype as (key: string) => string
                    ) ?? getProfessionLabel(professional.profession_type, t)}
                  </p>

                  {/* Company / law-firm affiliation. When the advocate is a
                      resolved member of a SAK office (#8021 v1), the firm name
                      links to its public office page; otherwise fall back to
                      the flat SAK `company_name` text. */}
                  {professional.law_firm_id ? (
                    <div className='flex items-center justify-center sm:justify-start gap-2 mb-3'>
                      <Building2 className='h-4 w-4 text-content-muted' />
                      <Link
                        href={{
                          pathname: '/law-offices/[id]',
                          params: { id: professional.law_firm_id },
                        }}
                        className='text-brand-tertiary/80 hover:text-brand-tertiary hover:underline'
                      >
                        {professional.law_firm_name ??
                          professional.company_name}
                      </Link>
                    </div>
                  ) : (
                    professional.company_name && (
                      <div className='flex items-center justify-center sm:justify-start gap-2 text-content-muted mb-3'>
                        <Building2 className='h-4 w-4' />
                        <span>{professional.company_name}</span>
                      </div>
                    )
                  )}

                  {/* Badges row -- hidden for suspended/removed profiles */}
                  {!isRestricted && (
                    <div className='flex flex-wrap items-center justify-center sm:justify-start gap-2'>
                      {professional.accepts_new_clients && (
                        <Badge className='bg-status-success/10 text-status-success border-status-success/30 hover:bg-status-success/20'>
                          <CheckCircle className='mr-1.5 h-3.5 w-3.5' />
                          {t('card.acceptingClients')}
                        </Badge>
                      )}

                      {/* #5850 / #6093: the Profil+ badge gates on the
                          server-derived CAPABILITY signal (``profile_badge``),
                          not ``subscription_tier`` equality (the #4736/#4792
                          plan-name-equality lesson). The by-id read path
                          (get_by_id.sql) now joins the owning user's plan and
                          projects the authoritative ``professional_profile_badge``
                          capability, so a bundled starter/plus (Profil+) lawyer
                          whose directory tier is still free gets the badge here
                          too. Fails closed (free/unclaimed → false → no badge). */}
                      {professional.profile_badge === true && (
                        <Badge className='bg-premium/10 text-premium'>
                          <Crown className='mr-1.5 h-3.5 w-3.5 fill-premium' />
                          {t('card.profilPlusBadge')}
                        </Badge>
                      )}

                      {/* Sample/demo profile marker — labels the seeded
                          example profile so visitors are never misled into
                          thinking it is a real, SAK-registered advocate.
                          Tooltip explains it previews a subscribed profile. */}
                      {professional.is_example_profile && (
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {/* Span (not Badge) so Radix can attach its ref —
                                  Badge is a plain function component. */}
                              <span className='inline-flex cursor-help items-center gap-1 rounded-full border border-line-subtle bg-surface-muted px-2.5 py-0.5 text-xs font-medium text-content-secondary'>
                                <Info className='h-3.5 w-3.5' />
                                {t('card.example')}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent
                              side='top'
                              className='max-w-xs text-center'
                            >
                              {t('card.exampleTooltip')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {professional.years_of_experience && (
                        <Badge
                          variant='outline'
                          className='text-content-secondary'
                          title={t('profile.yearsExperienceSelfReported')}
                        >
                          <Clock className='mr-1.5 h-3.5 w-3.5' />
                          {professional.years_of_experience}{' '}
                          {t('profile.yearsExperience')}
                        </Badge>
                      )}

                      {professional.response_time_hours && (
                        <Badge
                          variant='outline'
                          className='text-content-secondary'
                        >
                          <MessageSquare className='mr-1.5 h-3.5 w-3.5' />
                          {t('profile.profile.responseTime', {
                            hours: professional.response_time_hours,
                          })}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {contentUpdatedAt && (
            <div className='px-6 sm:px-8 pb-4 flex items-center gap-2 text-xs text-content-muted'>
              <Calendar className='h-3 w-3' />
              <span>
                {t('profile.lastUpdated')}:{' '}
                {new Date(contentUpdatedAt).toLocaleDateString(
                  locale === 'sk' ? 'sk-SK' : 'en-US'
                )}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content Grid — 2 columns, ordered by client importance */}
      <div className='grid gap-6 lg:grid-cols-2'>
        {/* Left Column — Contact & Expertise */}
        <div className='space-y-6'>
          {/* Contact Card */}
          {hasContactInfo && (
            <Card data-tour='profile-contact'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold flex items-center gap-2'>
                  <Phone className='h-4 w-4 text-brand-tertiary' />
                  {t('profile.contact')}
                </CardTitle>
              </CardHeader>
              <CardContent className='flex flex-col gap-3'>
                {/* Phone / mobile / email are click-to-reveal (anti-scraping);
                    the first reveal fires a contact_click engagement event. */}
                {professional.phone && (
                  <RevealContactRow
                    icon={<Phone className='h-4 w-4' />}
                    value={professional.phone}
                    href={`tel:${professional.phone}`}
                    revealLabel={t('profile.revealPhone')}
                    copyLabel={t('profile.copy')}
                    copiedLabel={t('profile.copied')}
                    professionalId={professional.id}
                  />
                )}

                {professional.mobile &&
                  professional.mobile !== professional.phone && (
                    <RevealContactRow
                      icon={<Smartphone className='h-4 w-4' />}
                      value={professional.mobile}
                      href={`tel:${professional.mobile}`}
                      revealLabel={t('profile.revealMobile')}
                      copyLabel={t('profile.copy')}
                      copiedLabel={t('profile.copied')}
                      professionalId={professional.id}
                    />
                  )}

                {professional.email && (
                  <RevealContactRow
                    icon={<Mail className='h-4 w-4' />}
                    value={professional.email}
                    href={`mailto:${professional.email}`}
                    revealLabel={t('profile.revealEmail')}
                    copyLabel={t('profile.copy')}
                    copiedLabel={t('profile.copied')}
                    professionalId={professional.id}
                  />
                )}

                {professional.website && (
                  <a
                    href={
                      professional.website.startsWith('http')
                        ? professional.website
                        : `https://${professional.website}`
                    }
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-3 min-h-[44px] text-sm text-content-secondary hover:text-brand-tertiary active:text-brand-tertiary transition-colors group'
                  >
                    <Globe className='h-4 w-4 text-content-muted group-hover:text-brand-tertiary flex-shrink-0' />
                    <span>{professional.website}</span>
                    <ExternalLink className='h-3 w-3 text-content-muted group-hover:text-brand-tertiary flex-shrink-0' />
                  </a>
                )}
              </CardContent>
            </Card>
          )}

          {/* Specializations -- hidden for removed profiles */}
          {!isRemoved && professional.specializations.length > 0 && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold flex items-center gap-2'>
                  <Briefcase className='h-4 w-4 text-brand-tertiary' />
                  {t('profile.specializations')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex flex-wrap gap-2'>
                  {professional.specializations.map((spec, idx) => (
                    <Badge
                      key={idx}
                      variant='secondary'
                      className='bg-surface-subtle text-content-secondary border border-line-subtle font-normal'
                    >
                      {spec}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Languages */}
          {professional.languages.length > 0 && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold flex items-center gap-2'>
                  <Globe className='h-4 w-4 text-brand-tertiary' />
                  {t('profile.profile.languages')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex flex-wrap gap-2'>
                  {professional.languages.map((lang, idx) => (
                    <Badge key={idx} variant='outline'>
                      {lang}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Practice Type / Additional Details -- hidden for removed profiles */}
          {!isRemoved &&
            (professional.practice_type || professional.price_range) && (
              <Card>
                <CardHeader className='pb-3'>
                  <CardTitle className='text-base font-semibold'>
                    {t('profile.details')}
                  </CardTitle>
                </CardHeader>
                <CardContent className='space-y-3'>
                  {professional.practice_type && (
                    <div className='flex items-center gap-3 text-sm'>
                      <Briefcase className='h-4 w-4 text-content-muted' />
                      <span className='text-content-secondary'>
                        {professional.practice_type}
                      </span>
                    </div>
                  )}
                  {professional.price_range && (
                    <div className='flex items-center gap-3 text-sm'>
                      <span className='text-content-muted'>Price range:</span>
                      <span className='text-content-secondary'>
                        {professional.price_range}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
        </div>

        {/* Right Column — Location & Background */}
        <div className='space-y-6'>
          {/* Address + Map Card */}
          {hasAddress && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold flex items-center gap-2'>
                  <MapPin className='h-4 w-4 text-brand-tertiary' />
                  {t('profile.address')}
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-3'>
                <div className='text-sm text-content-secondary'>
                  {professional.street && <p>{professional.street}</p>}
                  <p>
                    {[professional.postal_code, professional.city]
                      .filter(Boolean)
                      .join(' ')}
                  </p>
                  {professional.district && (
                    <p className='text-content-muted mt-1'>
                      {professional.district}
                    </p>
                  )}
                  {professional.country_code &&
                    professional.country_code !== 'SK' && (
                      <p className='text-content-muted mt-1'>
                        {getCountryName(professional.country_code, locale)}
                      </p>
                    )}
                </div>
                {professional.latitude != null &&
                  professional.longitude != null && (
                    <ProfileMiniMap
                      latitude={professional.latitude}
                      longitude={professional.longitude}
                      name={professional.name}
                      address={[
                        professional.street,
                        [professional.postal_code, professional.city]
                          .filter(Boolean)
                          .join(' '),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    />
                  )}
              </CardContent>
            </Card>
          )}

          {/* Bio -- hidden for removed profiles */}
          {!isRemoved && professional.bio && (
            <Card>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold'>
                  {t('profile.about')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className='text-sm text-content-secondary leading-relaxed whitespace-pre-wrap'>
                  {professional.bio}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Registry Info Card */}
          {professional.registry_source && (
            <Card data-tour='profile-registry'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-base font-semibold flex items-center gap-2'>
                  <Shield className='h-4 w-4 text-brand-tertiary' />
                  {t('profile.registryInfo')}
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-2'>
                <div className='flex justify-between text-sm'>
                  <span className='text-content-muted'>
                    {t('profile.registrySource')}
                  </span>
                  {sakUrl ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={sakUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-medium text-brand-tertiary hover:text-brand-tertiary/80 transition-colors'
                          >
                            {professional.registry_source.toUpperCase()}
                          </a>
                        </TooltipTrigger>
                        <TooltipContent side='top'>
                          {t('profile.registrySourceSakTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className='font-medium text-content-primary'>
                      {professional.registry_source.toUpperCase()}
                    </span>
                  )}
                </div>
                {professional.registry_number && (
                  <div className='flex justify-between text-sm'>
                    <span className='text-content-muted'>
                      {t('profile.registryId')}
                    </span>
                    {sakProfileUrl ? (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={sakProfileUrl}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='font-medium text-brand-tertiary hover:text-brand-tertiary/80 transition-colors'
                            >
                              {professional.registry_number}
                            </a>
                          </TooltipTrigger>
                          <TooltipContent side='top' className='max-w-64'>
                            {t('profile.viewInRegistryTooltip')}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className='font-medium text-content-primary'>
                        {professional.registry_number}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Empty state for unclaimed profiles -- not shown for suspended/removed */}
          {!isRestricted &&
            !professional.bio &&
            professional.specializations.length === 0 &&
            professional.languages.length === 0 && (
              <Card className='border-dashed'>
                <CardContent className='py-12 text-center'>
                  <div className='w-12 h-12 rounded-full bg-surface-subtle flex items-center justify-center mx-auto mb-4'>
                    <Briefcase className='h-6 w-6 text-content-muted' />
                  </div>
                  <p className='text-content-muted text-sm'>
                    {t('profile.emptyState.message')}
                  </p>
                  {/* Secondary claim nudge — public, same as the main banner:
                      visible to anonymous visitors on an unclaimed profile. */}
                  {!professional.is_claimed && (
                    <p className='text-content-muted text-xs mt-2'>
                      {t('profile.emptyState.claimNudge')}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}
        </div>
      </div>

      {/* Advocate blog — only renders when the author has published at least
          one post (BlogProfileTab is lazy by design). Mounted *after* the
          2-column main grid so it gets its own full-width row. */}
      <BlogProfileTab
        // Slug, not UUID, so the preview + "View all" links point straight at
        // the canonical /advokati/<slug>/blog/... URL. A UUID link only reaches
        // it through the middleware 308 (uuidProfileRedirect.ts): an extra hop
        // per click and internal links to non-canonical URLs for crawlers.
        professionalId={professional.slug || professional.id}
        locale={locale}
        posts={blogPosts}
      />

      {/* Product pitch for advocates (#11040). Placed last, below the profile
          and the blog: this page's first job is to answer the search that
          brought the visitor here, and the pitch reads as native only once
          that is done. It is self-identifying ("Ste advokát?") because the
          same page serves the public looking for a lawyer, and nothing in the
          request tells the two apart. Not gated on `isOwnProfile` — an
          advocate who has not claimed their listing is not signed in, and is
          exactly the reader this is for. */}
      <AdvocateProductPitch />
    </div>
  );
}

/**
 * Loading skeleton for the profile page.
 */
function ProfessionalProfileSkeleton() {
  return (
    <div className='container mx-auto px-4 py-6 max-w-5xl'>
      <Skeleton className='h-9 w-20 mb-4' />

      {/* Hero skeleton */}
      <Card className='mb-6'>
        <CardContent className='p-0'>
          <div className='h-2 bg-gradient-to-r from-surface-subtle to-surface-subtle' />
          <div className='p-6 sm:p-8'>
            <div className='flex flex-col lg:flex-row gap-6'>
              <div className='flex flex-col sm:flex-row gap-5 flex-1'>
                <Skeleton className='h-28 w-28 sm:h-32 sm:w-32 rounded-full mx-auto sm:mx-0' />
                <div className='flex-1 space-y-3'>
                  <Skeleton className='h-8 w-3/4 mx-auto sm:mx-0' />
                  <Skeleton className='h-5 w-1/3 mx-auto sm:mx-0' />
                  <Skeleton className='h-5 w-1/2 mx-auto sm:mx-0' />
                  <div className='flex gap-2 justify-center sm:justify-start'>
                    <Skeleton className='h-6 w-32 rounded-full' />
                    <Skeleton className='h-6 w-24 rounded-full' />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content skeleton */}
      <div className='grid gap-6 lg:grid-cols-2'>
        <div className='space-y-6'>
          <Card>
            <CardHeader className='pb-3'>
              <Skeleton className='h-5 w-24' />
            </CardHeader>
            <CardContent className='space-y-3'>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-3/4' />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='pb-3'>
              <Skeleton className='h-5 w-32' />
            </CardHeader>
            <CardContent>
              <div className='flex flex-wrap gap-2'>
                <Skeleton className='h-6 w-24 rounded-full' />
                <Skeleton className='h-6 w-32 rounded-full' />
                <Skeleton className='h-6 w-20 rounded-full' />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className='space-y-6'>
          <Card>
            <CardHeader className='pb-3'>
              <Skeleton className='h-5 w-20' />
            </CardHeader>
            <CardContent className='space-y-3'>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-2/3' />
              <Skeleton className='h-[160px] w-full rounded-lg' />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='pb-3'>
              <Skeleton className='h-5 w-28' />
            </CardHeader>
            <CardContent>
              <Skeleton className='h-4 w-full' />
              <Skeleton className='h-4 w-2/3 mt-2' />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
